import "./member-request.css";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import qwimage from "../../images/qr1.jpeg";
import {
  fetchMembershipData,
  fetchProfileDashboard,
  submitMembershipRequest,
  createMembershipOrder
} from "../../api/membership.api";

const MembershipRequestPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const activationIntervalRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    Promise.all([
      fetchMembershipData(),
      fetchProfileDashboard(),
    ])
      .then(([membershipData, profileData]) => {
        if (!mounted) return;

        const selected = membershipData.membership_plans.find(
          (p) => p.id === Number(id)
        );

        setPlan(selected || null);
        setUser(profileData.personal_info);
      })
      .catch(() => {
        if (!mounted) return;
        setPlan(null);
        setUser(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  const clearActivationPolling = () => {
    if (activationIntervalRef.current) {
      clearInterval(activationIntervalRef.current);
      activationIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearActivationPolling();
    };
  }, []);

  const handlePayment = async () => {
    if (!plan || !user || submitting || paymentStatus === "processing") return;
    setSubmitting(true);
    setError("");

    try {
      const isLifetimePlan = !plan?.duration_days;
      const order = await createMembershipOrder({
        membership_id: plan.id,
        referral_code: isLifetimePlan ? referralCode.trim() : "",
      });

      if (!window.Razorpay) {
        throw new Error("Payment gateway is still loading. Please try again.");
      }

      const options = {
        key: order.key,
        amount: order.amount,
        currency: "INR",
        name: "164DiecastZone",
        description: `Upgrade to ${plan.name}`,
        order_id: order.order_id,

        readonly: {
          email: true,
          contact: true
        },


        handler: function (response) {
          setPaymentStatus("processing");
          setSubmitting(false);
          checkMembershipActivation();
        },
        modal: {
          ondismiss: function () {
            setPaymentStatus("cancelled");
            setSubmitting(false);
            clearActivationPolling();
          },
        },
        prefill: {
          name: user.first_name,
          email: user.email,
        },

        theme: {
          color: "#FFA726",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function () {
        setPaymentStatus("failed");
        setSubmitting(false);
        clearActivationPolling();
      });
      rzp.open();

    } catch (error) {
      console.error(error);
      setError(error?.message || "Unable to start payment. Please try again.");
      setSubmitting(false);
    }
  };
  useEffect(() => {
    if (paymentStatus === "success") {
      const timer = setTimeout(() => {
        window.location.href = "/membership";
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  const checkMembershipActivation = () => {
    clearActivationPolling();
    let attempts = 0;

    activationIntervalRef.current = setInterval(async () => {
      attempts++;

      try {
        const data = await fetchMembershipData();

        if (data.current_membership) {
          clearActivationPolling();
          setPaymentStatus("success");
          setSubmitting(false);
          return;
        }

        if (attempts > 20) {
          clearActivationPolling();
          setPaymentStatus("failed");
          setError("Payment received, but membership activation is delayed. Please check again in a minute.");
          setSubmitting(false);
        }

      } catch (err) {
        clearActivationPolling();
        setPaymentStatus("failed");
        setError("Unable to verify payment status right now. Please try again.");
        setSubmitting(false);
      }

    }, 3000);
  };


  if (loading) return <div className="page">
    <div className="loading-wrap">
      <div className="loader" />
      <div>Loading...</div>
    </div>
  </div>;

  if (!plan) {
    return (
      <div className="membership-request-page">
        <p>Invalid plan selected.</p>
        <button
          className="submit-btn"
          onClick={() => navigate("/membership")}
        >
          Back to Membership
        </button>
      </div>
    );
  }

  const isLifetimePlan = !plan?.duration_days;



  const handleSubmitRequest = async () => {
    setError("");
    setSubmitting(true);

    try {
      await submitMembershipRequest({
        membership_type: plan.id,
        payment_reference: "UPI_QR",
      });

      setSubmitted(true);
    } catch (err) {
      setError(
        err?.error ||
        err?.detail ||
        "Something went wrong"
      );
    } finally {
      setSubmitting(false);
    }
  };


  if (paymentStatus === "success") {
    return (
      <div className="payment-result success">
        <div className="result-card">
          <div className="icon success-icon">✓</div>
          <h2>Payment Successful</h2>
          <p>Your membership is being activated.</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === "cancelled") {
    return (
      <div className="payment-result warning">
        <div className="result-card">
          <div className="icon warning-icon">!</div>
          <h2>Payment Cancelled</h2>
          <p>You cancelled the payment process.</p>
          <button
            className="primary-btn"
            onClick={() => setPaymentStatus(null)}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === "failed") {
    return (
      <div className="payment-result error">
        <div className="result-card">
          <div className="icon error-icon">✕</div>
          <h2>Payment Failed</h2>
          <p>Something went wrong. Please try again after some time.</p>
          <button
            className="primary-btn"
            onClick={() => setPaymentStatus(null)}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  if (paymentStatus === "processing") {
    return (
      <div className="payment-result">
        <div className="result-card">
          <div className="spinner"></div>
          <h2>Payment Successful</h2>
          <p>Activating your membership...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="membership-request-page">
      <h1>Membership Checkout</h1>

      {/* MEMBER DETAILS */}
      <section className="summary-card">
        <h3>Member Details</h3>
        <div className="summary-row">
          <span>Name</span>
          <strong>{user?.first_name || "—"}</strong>
        </div>
        <div className="summary-row">
          <span>Email</span>
          <strong>{user?.email || "—"}</strong>
        </div>
      </section>

      {/* PLAN DETAILS */}
      <section className="summary-card plan-summary">
        <h3>Selected Plan</h3>

        <strong>{plan.name}</strong>
        <p className="plan-price">₹{plan.price}</p>

        <ul className="benefit-list">
          {plan.benefits.map((b) => (
            <li key={b.id}>✓ {b.description}</li>
          ))}
        </ul>
      </section>

      {isLifetimePlan && (
        <section className="summary-card">
          <h3>Referral Code</h3>
          <div className="membership-request-field">
            <label htmlFor="membership-referral-code">
              Enter Referral Code (Optional)
            </label>
            <input
              id="membership-referral-code"
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="Enter referral code"
              maxLength={50}
            />
          </div>
        </section>
      )}

      {/* PAYMENT */}
      {/* <section className="summary-card qr-section">
        <h3>Complete Payment</h3>

        <p className="payment-note">
          Scan the QR code using any UPI app and complete payment.
        </p>

        <img
          src={qwimage}
          alt="UPI QR"
          className="qr-image"
        />

        <div className="payment-warning">
          After payment, click below to submit request.
        </div>
      </section> */}

      {error && <div className="error-message">{error}</div>}

      <button
        className="submit-btn"
        disabled={submitting}
        onClick={handlePayment}
      >
        {submitting
          ? "Processing..."
          : "Click to Pay"}
      </button>
    </div>
  );
};

export default MembershipRequestPage;
